import { useState, useContext, useEffect } from 'react';
import gql from 'graphql-tag';
import _ from 'lodash';

import MercuryContext from 'ConnectionManager/mercuryContext';

const queryCompetencyPerformance = gql`
query getCompetencyPerformance($cohortId: UUID!, $documentId: UUID!, $documentItemId: UUID!) {
  cohortById(cohortId: $cohortId) {
    id
    enrollments {
      edges {
        node {
          account {
            id
          }
          learnerPerformanceByCompetency(documentId: $documentId, documentItemId: $documentItemId) {
            value
          }
        }
      }
    }
  }
}
`;

/**
 * Loads the performance data for a list of documentItems. The cohort id and deployment id are
 * only used for auth and dont impact the result.
 * @param {string} cohortId The cohort id
 * @param {string} deploymentId The deployment id
 * @param {DocumentItem[]} documentItems An array of document items.
 * @returns {{ errors: Object[], data: Object }}
 * An apollo state object containing list of errors, and the fetched data (A map of documentItemIds
 * to performance values).
 */
export default function useDocumentItemsPerformance(cohortId, deploymentId, documentItems) {
  const [data, setData] = useState({});
  const [errors, setErrors] = useState(null);
  const { apolloClient } = useContext(MercuryContext);

  useEffect(() => {
    // Clear errors, but persist data
    setErrors(null);

    if (!documentItems) {
      return;
    }

    documentItems.forEach((documentItem) => {
      apolloClient.query({
        query: queryCompetencyPerformance,
        fetchPolicy: 'cache-first',
        variables: {
          cohortId,
          deploymentId,
          documentId: documentItem.documentId,
          documentItemId: documentItem.id,
        },
      })
        .then((response) => {
          setData((prevData) => ({
            ...prevData,
            [documentItem.id]: _.chain(response)
              .get('data.cohortById.enrollments.edges')
              .map(({ node }) => node)
              .value(),
          }));
        })
        .catch((error) => setErrors((prevErrors = []) => [...prevErrors, error]));
    });
  }, [apolloClient, cohortId, deploymentId, documentItems]);

  return { errors, data };
}
